import React from 'react';
import type { SectionProps } from './types';
import { UnitsSection } from './UnitsSection';

const formatDateForInput = (isoDate: string | undefined): string => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const TimerSettingsSection: React.FC<SectionProps> = ({ data, setFormData }) => {
  return (
    <div className="border-b pb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Countdown Timer</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Date & Time *
          </label>
          <input
            type="datetime-local"
            value={formatDateForInput(data.targetDate)}
            onChange={e => {
              const date = new Date(e.target.value);
              setFormData({ ...data, targetDate: date.toISOString() });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Display Format</label>
            <select
              value={data.displayFormat || 'full'}
              onChange={e => setFormData({ ...data, displayFormat: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="full">Full</option>
              <option value="compact">Compact</option>
              <option value="minimal">Minimal</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
            <select
              value={data.layout || 'horizontal'}
              onChange={e => setFormData({ ...data, layout: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="horizontal">Horizontal</option>
              <option value="vertical">Vertical</option>
              <option value="grid">Grid</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Alignment</label>
            <select
              value={data.alignment || 'center'}
              onChange={e => setFormData({ ...data, alignment: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
            <select
              value={data.size || 'medium'}
              onChange={e => setFormData({ ...data, size: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
        </div>

        <UnitsSection data={data} setFormData={setFormData} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Number Style</label>
            <select
              value={data.numberStyle || 'default'}
              onChange={e => setFormData({ ...data, numberStyle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="default">Default</option>
              <option value="bold">Bold</option>
              <option value="card">Card</option>
              <option value="gradient">Gradient</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Separator Style</label>
            <select
              value={data.separatorStyle || 'colon'}
              onChange={e => setFormData({ ...data, separatorStyle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="colon">Colon (:)</option>
              <option value="slash">Slash (/)</option>
              <option value="dot">Dot (·)</option>
              <option value="none">None</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={data.showLabels !== false}
              onChange={e => setFormData({ ...data, showLabels: e.target.checked })}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Show Labels (Days, Hours, etc.)</span>
          </label>
        </div>
      </div>
    </div>
  );
};
